/**
 * Gerenciador de Logs Estruturados
 * 
 * Responsável por:
 * - Logs estruturados de envio de mensagens
 * - Logs estruturados de resposta
 * - Mascarar secrets automaticamente
 * - Logs específicos por action
 * - Controlar verbosidade
 * 
 * @module LogManager
 */

import { WebViewMessage, WebViewResponse } from '../../types/messages';
import { sendToTestLog, sendToTerminal } from '../../../terminalLogger';
import { maskSecrets } from '../../util/logging';
import { 
  LogCategory, 
  LogLevel,
  getLogLevel, 
  shouldLog, 
  shouldIncludeJson, 
  shouldLogAuthDetails,
  createPayloadSummary
} from '@/config/logging';

/**
 * Gerenciador de logs para comunicação
 */
export class LogManager {
  private readonly isDevelopmentMode: boolean;

  constructor() {
    this.isDevelopmentMode = process.env.NODE_ENV === 'development';
  }

  /**
   * Log de envio de mensagem
   * 
   * @param action Action sendo enviada
   * @param requestId ID da requisição
   * @param payload Payload completo
   */
  public logSend(action: string, requestId: string, payload: any): void {
    if (!this.isDevelopmentMode) return;
    
    const level = getLogLevel();
    
    // Health checks apenas no nível 3 (DEBUG)
    if (action === 'health:pong' && level !== LogLevel.DEBUG) {
      return;
    }
    
    // ✅ CORREÇÃO: Logs de comunicação C# devem aparecer no nível 2 também
    // Usar CONNECTION ao invés de PAYLOAD para aparecer no nível 2
    // Mas manter PAYLOAD para nível 3 (debug completo)
    const useConnectionCategory = level === LogLevel.OPERATIONAL;
    const logCategory = useConnectionCategory ? LogCategory.CONNECTION : LogCategory.PAYLOAD;
    
    if (!shouldLog(logCategory, level)) {
      return;
    }

    try {
      const masked = maskSecrets(payload.data || {});
      let logData: any;
      
      // Nível 1: apenas resumo
      if (level === LogLevel.MINIMAL) {
        logData = { action, requestId };
      }
      // Nível 2: resumo + payload JSON organizado
      else if (level === LogLevel.OPERATIONAL) {
        logData = {
          action,
          requestId,
          payload: {
            // Apenas campos principais organizados
            ...(masked.comando ? { comando: masked.comando } : {}),
            ...(masked.caminhoArquivo ? { caminhoArquivo: masked.caminhoArquivo } : {}),
            ...(masked.timestamp ? { timestamp: masked.timestamp } : {}),
            ...(masked.apiOnline !== undefined ? { apiOnline: masked.apiOnline } : {})
          }
        };
      }
      // Nível 3: payload completo
      else {
        logData = { action, requestId, payload: masked };
      }
      
      sendToTestLog('CSHARP:SEND', logData, 'csharp', logCategory);
    } catch (error) {
      // Ignorar erros de logging
    }
  }

  /**
   * Log de recebimento de resposta
   * 
   * @param response Resposta recebida
   * @param originalAction Action original (se disponível)
   */
  public logReceive(response: WebViewResponse, originalAction?: string): void {
    if (!this.isDevelopmentMode) return;
    
    const level = getLogLevel();
    const action = originalAction || (response as any)?.action;
    
    // Health checks apenas no nível 3 (DEBUG)
    if (action === 'health:pong' && level !== LogLevel.DEBUG) {
      return;
    }
    
    // ✅ CORREÇÃO: Logs de comunicação C# devem aparecer no nível 2 também
    // Usar CONNECTION ao invés de PAYLOAD para aparecer no nível 2
    // Mas manter PAYLOAD para nível 3 (debug completo)
    const useConnectionCategory = level === LogLevel.OPERATIONAL;
    const logCategory = useConnectionCategory ? LogCategory.CONNECTION : LogCategory.PAYLOAD;
    
    if (!shouldLog(logCategory, level)) {
      return;
    }

    try {
      let logData: any;
      
      // Nível 1: apenas resumo
      if (level === LogLevel.MINIMAL) {
        logData = {
          action,
          requestId: (response as any)?.requestId,
          status: (response as any)?.status
        };
      }
      // Nível 2: resumo + payload JSON organizado
      else if (level === LogLevel.OPERATIONAL) {
        const details = (response as any)?.result ?? (response as any)?.data;
        const masked = maskSecrets(details);
        
        logData = {
          action,
          requestId: (response as any)?.requestId,
          status: (response as any)?.status,
          message: (response as any)?.message,
          payload: masked ? {
            // Apenas campos principais organizados
            ...(masked.comando ? { comando: masked.comando } : {}),
            ...(masked.caminhoArquivo ? { caminhoArquivo: masked.caminhoArquivo } : {}),
            ...(masked.timestamp ? { timestamp: masked.timestamp } : {}),
            ...(masked.apiOnline !== undefined ? { apiOnline: masked.apiOnline } : {})
          } : undefined
        };
      }
      // Nível 3: payload completo
      else {
        const details = (response as any)?.result ?? (response as any)?.data;
        const masked = maskSecrets(details);
        logData = {
          action,
          requestId: (response as any)?.requestId,
          status: (response as any)?.status,
          message: (response as any)?.message,
          hasResult: !!(response as any)?.result,
          hasData: !!(response as any)?.data,
          payload: masked
        };
      }
      
      sendToTestLog('CSHARP:RECV', logData, 'csharp', logCategory);
    } catch (error) {
      // Ignorar erros de logging
    }
  }

  /**
   * Log detalhado do payload completo antes de enviar
   * 
   * @param message Mensagem completa
   * @param payloadSize Tamanho do payload em bytes
   */
  public logPayload(message: WebViewMessage, payloadSize: number): void {
    const level = getLogLevel();
    const parsedForLog = message as any;
    const action = parsedForLog.action;
    
    // Health checks apenas no nível 3 (DEBUG)
    if (action === 'health:pong' && level !== LogLevel.DEBUG) {
      return;
    }
    
    // Verificar se deve logar categoria PAYLOAD no nível atual
    if (!shouldLog(LogCategory.PAYLOAD, level)) {
      return;
    }
    
    try {
      let logData: any;
      
      // Nível 1: apenas resumo (action, requestId, payloadSize)
      if (level === LogLevel.MINIMAL) {
        logData = {
          action,
          requestId: parsedForLog.requestId,
          payloadSize
        };
      }
      // Nível 2: resumo + payload JSON organizado
      else if (level === LogLevel.OPERATIONAL) {
        logData = {
          action,
          requestId: parsedForLog.requestId,
          payloadSize,
          data: parsedForLog.data ? {
            // Incluir apenas campos principais, organizados
            ...(parsedForLog.data.comando ? { comando: parsedForLog.data.comando } : {}),
            ...(parsedForLog.data.caminhoArquivo ? { caminhoArquivo: parsedForLog.data.caminhoArquivo } : {}),
            ...(parsedForLog.data.timestamp ? { timestamp: parsedForLog.data.timestamp } : {}),
            ...(parsedForLog.data.apiOnline !== undefined ? { apiOnline: parsedForLog.data.apiOnline } : {})
          } : undefined
        };
      }
      // Nível 3: JSON completo
      else if (shouldIncludeJson(level)) {
        // Criar versão mascarada para log seguro
        logData = {
          action: parsedForLog.action,
          requestId: parsedForLog.requestId,
          sessionToken: parsedForLog.sessionToken
            ? `${parsedForLog.sessionToken.substring(0, 8)}...`
            : 'AUSENTE',
          data: parsedForLog.data
            ? {
                auth: parsedForLog.data.auth
                  ? {
                      signature: parsedForLog.data.auth.signature
                        ? `${parsedForLog.data.auth.signature.substring(0, 16)}...`
                        : 'AUSENTE',
                      nonce: parsedForLog.data.auth.nonce || 'AUSENTE',
                      timestamp: parsedForLog.data.auth.timestamp || 'AUSENTE'
                    }
                  : 'AUSENTE ❌',
                // Mascarar outros dados sensíveis se existirem
                ...Object.keys(parsedForLog.data)
                  .filter(k => k !== 'auth')
                  .reduce((acc, key) => {
                    if (key === 'bootstrap' && parsedForLog.data[key]?.accessToken) {
                      acc[key] = {
                        ...parsedForLog.data[key],
                        accessToken: `${parsedForLog.data[key].accessToken.substring(0, 8)}...`
                      };
                    } else if (key === 'sessionToken') {
                      acc[key] = `${String(parsedForLog.data[key]).substring(0, 8)}...`;
                    } else {
                      acc[key] = parsedForLog.data[key];
                    }
                    return acc;
                  }, {} as any)
              }
            : 'AUSENTE',
          payloadSize,
          spec: 'Payload exato enviado ao backend C# ✅'
        };
      }

      // Apenas logar payload completo no nível DEBUG (3)
      if (shouldLog(LogCategory.PAYLOAD, getLogLevel())) {
        sendToTerminal('📤 [C#:PAYLOAD_COMPLETO]', logData, LogCategory.PAYLOAD);
      }
    } catch (error) {
      sendToTerminal('❌ [PAYLOAD:LOG_ERROR]', { error: String(error) }, LogCategory.ERROR);
    }
  }

  /**
   * Log específico para action open-tools-docker
   * 
   * @param message Mensagem completa
   */
  public logOpenToolsDocker(message: WebViewMessage): void {
    try {
      const parsedForLog = message as any;

      sendToTerminal('🚀 [OPEN_TOOLS:PAYLOAD_DETALHADO]', {
        action: parsedForLog.action,
        requestId: parsedForLog.requestId,
        hasSessionToken: !!parsedForLog.sessionToken,
        hasDataAuth: !!(parsedForLog.data && parsedForLog.data.auth),
        authLocation: parsedForLog.data?.auth ? 'data.auth ✅' : 'AUSENTE ❌',
        hasSignature: !!(parsedForLog.data?.auth?.signature),
        hasNonce: !!(parsedForLog.data?.auth?.nonce),
        hasTimestamp: !!(parsedForLog.data?.auth?.timestamp),
        nonceFormat: parsedForLog.data?.auth?.nonce
          ? parsedForLog.data.auth.nonce.includes('-')
            ? 'UUID v4 ✅'
            : 'FORMATO INVÁLIDO ❌'
          : 'AUSENTE ❌',
        timestampType: typeof parsedForLog.data?.auth?.timestamp,
        timestampValue: parsedForLog.data?.auth?.timestamp,
        signatureFormat: parsedForLog.data?.auth?.signature ? 'Base64 ✅' : 'AUSENTE ❌',
        dataKeys: parsedForLog.data ? Object.keys(parsedForLog.data).filter(k => k !== 'auth') : [],
        hasDataUrl: !!(parsedForLog.data && parsedForLog.data.url),
        dataUrlValue: parsedForLog.data?.url
          ? String(parsedForLog.data.url).substring(0, 100)
          : 'AUSENTE ❌'
      });
    } catch (error) {
      sendToTerminal('❌ [OPEN_TOOLS:LOG_ERROR]', { error: String(error) });
    }
  }

  /**
   * Log de token de sessão
   * 
   * @param action Action que está usando o token
   * @param hasToken Se o token está presente
   */
  public logSessionToken(action: string, hasToken: boolean, tokenPreview?: string): void {
    const level = getLogLevel();
    
    // Apenas nível 3 loga detalhes de autenticação
    if (!shouldLogAuthDetails(level)) {
      return;
    }
    
    sendToTerminal('🔑 [SESSION_TOKEN]', {
      action,
      hasToken,
      tokenSource: 'window.sessionToken',
      tokenPreview: tokenPreview || (hasToken ? 'PRESENTE' : 'AUSENTE')
    }, LogCategory.AUTH);
  }

  /**
   * Log de injeção de HMAC
   * 
   * @param action Action
   * @param requestId Request ID
   * @param auth Dados de autenticação
   */
  public logHmacInjection(action: string, requestId: string, auth: any): void {
    const level = getLogLevel();
    
    // Apenas nível 3 loga detalhes de autenticação
    if (!shouldLogAuthDetails(level)) {
      return;
    }
    
    sendToTerminal('✅ [HMAC:INJECTED]', {
      action,
      requestId,
      location: 'data.auth ✅',
      hasSignature: true,
      hasNonce: true,
      hasTimestamp: true,
      signatureFormat: 'Base64 ✅',
      nonceFormat: 'UUID v4 ✅',
      noncePreview: auth.nonce?.substring(0, 8) + '...',
      timestamp: auth.timestamp,
      timestampFormat: 'Unix seconds ✅',
      mode: process.env.NODE_ENV,
      required: true,
      spec: 'webview-rules.md CORRETO ✅'
    }, LogCategory.AUTH);
  }

  /**
   * Log de HMAC bloqueado/indisponível
   * 
   * @param action Action
   * @param requestId Request ID
   */
  public logHmacBlocked(action: string, requestId: string): void {
    // Erros sempre são logados (categoria ERROR)
    sendToTerminal('🚫 [HMAC:REQUIRED_BUT_UNAVAILABLE]', {
      action,
      requestId,
      reason: 'window.sessionCrypto.hmacKeyB64 not found',
      mode: process.env.NODE_ENV,
      blocked: true,
      help: 'Execute esta aplicação dentro do CorelDRAW WebView para ter acesso às variáveis de autenticação.'
    }, LogCategory.ERROR);
  }

  /**
   * Log de HMAC isento
   * 
   * @param action Action isenta
   * @param requestId Request ID
   */
  public logHmacExempt(action: string, requestId: string): void {
    const level = getLogLevel();
    
    // Filtrar health:pong mesmo no nível 3
    if (action === 'health:pong') {
      return;
    }
    
    // Apenas nível 3 loga detalhes de autenticação
    if (!shouldLogAuthDetails(level)) {
      return;
    }
    
    sendToTerminal('ℹ️ [HMAC:EXEMPT]', {
      action,
      requestId,
      reason: 'Action não requer HMAC',
      mode: process.env.NODE_ENV
    }, LogCategory.AUTH);
  }

  /**
   * Log de erro ao gerar HMAC
   * 
   * @param action Action
   * @param requestId Request ID
   * @param error Erro
   */
  public logHmacError(action: string, requestId: string, error: any): void {
    // Erros sempre são logados (categoria ERROR)
    sendToTerminal('❌ [HMAC:GENERATION_ERROR]', {
      action,
      requestId,
      error: error instanceof Error ? error.message : String(error),
      blocked: true
    }, LogCategory.ERROR);
  }

  /**
   * Verifica se está em modo de desenvolvimento
   */
  public isDevelopment(): boolean {
    return this.isDevelopmentMode;
  }
}

